import { Injectable, signal } from '@angular/core';

import { AssessmentUseCaseEnum } from '../models/assessments.enum';

/**
 * Source of truth for the Unified Assessments use-case tab (Privacy / Vendor / AI). The tab lives in
 * the route PATH (`/assessments/vendor`) rather than the `?view=` query param, so it survives internal
 * view/filter changes. The tab is a persistent
 * "world": its label is pinned as a hard filter scope on the table and the rail counters, and every
 * rail category / ad-hoc filter layers on top of it. The `All` tab (base `/assessments`) has no base
 * label; saved views navigate back to `/assessments`, so selecting one clears the tab.
 */
@Injectable({ providedIn: 'root' })
export class AssessmentsUseCaseTabService {
    /** Route-path slug per use-case tab. `All` has no slug (it is the base `/assessments`). */
    static readonly slugByLabel: Readonly<Record<string, string>> = {
        [AssessmentUseCaseEnum.Privacy]: 'privacy',
        [AssessmentUseCaseEnum.Vendor]: 'vendor',
        [AssessmentUseCaseEnum.AI]: 'ai',
    };

    /** The built-in use-case labels — the ones reachable as tabs. Kept as a set for quick lookups. */
    static readonly useCaseLabels: ReadonlySet<string> = new Set(Object.keys(AssessmentsUseCaseTabService.slugByLabel));

    private readonly activeTabLabelSignal = signal<string | null>(null);
    /** Active tab's label (`Privacy`/`Vendor`/`AI`) or `null` for the All tab / no tab. */
    readonly activeTabLabel = this.activeTabLabelSignal.asReadonly();

    setActiveTabLabel(label: string | null | undefined): void {
        this.activeTabLabelSignal.set(label || null);
    }

    slugForLabel(label: string): string | null {
        return AssessmentsUseCaseTabService.slugByLabel[label] ?? null;
    }
}
